import { EditableItemContextType, log } from "@repo/helpers";
import { Loading } from "@repo/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import DynamicTab from ".";
import { useSettingsApi } from "../../../api/settingsApi";
import { dynamicEditorPageName } from "../../../string";
import useTemplateData, { ITemplateData } from "../templateDataHook";
import { getValidator } from "../validation";

interface DTProps<TEntity, TTemplateData extends ITemplateData> {
	itemContext: EditableItemContextType<TEntity>;
	pageName: string & dynamicEditorPageName;
	itemProp: string & keyof TTemplateData;
	otherTemplateData?: Partial<TTemplateData>;
	loaded: boolean;
	save: (email: boolean) => Promise<TEntity>;
	postSave?: () => Promise<void>;
	updateValues: (
		id: string,
		value: any,
		newState: TTemplateData,
		parentComponent?: string,
	) => Promise<{ changed: boolean; newState: TTemplateData }>;
	customFunctions?: (name: string) => () => Promise<void>;
	readonly?: boolean;
	containerClass?: string;
	submitButtonText?: string;
	saveAndEmail?: boolean;
}

export function EditableItemDynamicTab<T, TTemplateData extends ITemplateData>(props: DTProps<T, TTemplateData>) {
	const { itemContext, otherTemplateData, pageName, itemProp, loaded, submitButtonText, saveAndEmail } = props;
	const { updateValues, customFunctions, postSave } = props;
	const { item, editItem, updateItem, overwriteItem, clearChanges, pendingChanges } = itemContext;
	const { useSettingsQuery, useDynamicComponentsQuery } = useSettingsApi();
	const { loaded: settingsLoaded } = useSettingsQuery();
	const { data: dynamicComponents, loaded: dynamicComponentsLoaded } = useDynamicComponentsQuery();
	const [initialized, setInitialized] = useState(settingsLoaded);

	useEffect(() => {
		settingsLoaded && setInitialized(true);
	}, [settingsLoaded]);
	const allLoaded = settingsLoaded && dynamicComponentsLoaded && loaded;
	const customTemplateData = useMemo(() => ({ ...otherTemplateData }) as TTemplateData, [otherTemplateData]);

	const { templateData, dirty } = useTemplateData<TTemplateData>(customTemplateData);
	const validator = useMemo(() => getValidator(item), [item]);
	const updateData = useCallback((s: TTemplateData) => updateItem(s[itemProp] as any), [updateItem]);
	const editData = useCallback((s: TTemplateData) => editItem(s[itemProp] as any), [editItem]);
	const sections = useMemo(
		() => dynamicComponents?.filter((s) => s.pageName?.includes(pageName)),
		[dynamicComponents, pageName],
	);

	const save = useCallback(
		async (email: boolean) => {
			try {
				const newItem = await props.save(email);
				if (newItem) {
					overwriteItem(newItem);
				}
			} catch (ex) {
				log(ex, "error");
			}
		},
		[item, pendingChanges, overwriteItem, props.save],
	);

	return (
		<Loading loading={!allLoaded}>
			<DynamicTab<TTemplateData>
				loaded={loaded}
				sections={sections}
				validator={validator}
				updateData={updateData}
				editData={editData}
				updateValues={updateValues}
				initialized={initialized}
				entryDisabled={!allLoaded || props.readonly}
				templateData={templateData}
				dirty={dirty}
				cancel={pendingChanges && clearChanges}
				name={pageName}
				save={save}
				postSave={postSave}
				customFunctions={customFunctions}
				containerClass={props.containerClass}
				submitButtonText={submitButtonText}
				saveAndEmail={saveAndEmail}
			/>
		</Loading>
	);
}
